// frontend/src/lib/copyResolve.ts
// #1039 / AP-26 · D-CS-3. The fallback chain, as pure rules.
//
//     override ?? last_known_good ?? seed
//
// WHY THERE IS A CACHE AT ALL. The seed is not a known-good current value — it
// is a code default that can rot (GH-620 shipped a TRF URL that 404'd) and that
// a later deploy can correct underneath a live override. So a failed read must
// not drop straight to the bundle; it should first try the last wording this
// browser actually saw TRF publish. react-query's in-memory cache cannot do
// that job: it dies on reload, and a reload is exactly when the API-down path
// is taken, so an unnamed cache would be empty on the one occasion it exists
// for. Hence localStorage, keyed by document slug.
//
// WHY THE CACHE IS BOUNDED. The mirror hazard: a browser holding pre-correction
// wording re-renders a retracted string indefinitely, with no server-side
// invalidation — a hole exactly where ruling #1030's accepted risk lands
// ("a wrong string can reach a family before anyone else sees it"). Two bounds,
// and each one alone is enough to refuse:
//
//   1. `fetched_at` within 24 hours.
//   2. `version_n >= min_copy_version`, the document floor. Publishing raises
//      the floor to the version it just published (`CopyDocument.promotePublished`
//      does it with `$max` in the same update), so a correction force-expires
//      every cached copy of the string it replaced.
//
// The floor is stored WITH the record. It has to be: the moment the cache is
// used is the moment the server is unreachable, so there is no fresh floor to
// compare against, and a floor the client cannot remember cannot be enforced.
// A fresh floor, when one is available, wins over the stored one.
import { COPY_SLOTS, type CopySlotKey, type ComposedValue, type LinkedValue } from './copySlots';

/** D-CS-3.2. A day is the outer bound on how long a browser may keep serving
 *  copy it can no longer confirm. */
export const COPY_LKG_TTL_MS = 24 * 60 * 60 * 1000;

/** react-query staleTime for the published-copy read. AP-26 gives TRF a
 *  60-SECOND budget from publish to family screen; the design forbids a server
 *  cache, so the whole budget is spent here and this value has to stay under it
 *  with room for the round trip. Asserted by test — see useCopy.test.tsx. */
export const COPY_STALE_TIME_MS = 30_000;

/** Kept in lockstep with `ALLOWED_COPY_HOSTS` in backend/src/models/copySlots.js.
 *  The generated artifact carries this list and the backend contract test
 *  asserts the two agree, so the producer and the consumer cannot drift. */
export const ALLOWED_COPY_HOSTS = Object.freeze(['trf.org', 'www.trf.org']);

/** 2026-11-03: a copy link may also write to a TRF address — `mailto:` one
 *  `@trf.org` mailbox, no query (no prefilled subject or extra recipients).
 *  Kept in lockstep with `isTrfMailto` in backend/src/models/copyLocks.js. */
export const TRF_MAILTO = /^mailto:[a-z0-9._%+-]+@trf\.org$/i;
export const isTrfMailto = (href: string): boolean => TRF_MAILTO.test(href);

export const copyLkgKey = (slug: string) => `trf.copy.lkg.${slug}`;
export const COPY_FALLBACK_COUNTER_KEY = 'trf.copy.fallbacks';

export interface CopyPayload {
  version_n: number;
  min_copy_version: number;
  values: Record<string, unknown>;
}

export interface CopyLkgRecord extends CopyPayload {
  fetched_at: number;
}

/** What `GET /api/copy/published/:slug` returns. Overrides only — a key absent
 *  here means "the seed is current", which is why an empty response is not an
 *  empty screen. */
export interface CopyPublishedResponse extends CopyPayload {
  slug: string;
}

/** Which limb of the chain answered. `published` is not a fallback. */
export type CopyOrigin = 'published' | 'last_known_good' | 'seed';

export interface CopyFallbackCounts {
  seed: number;
  last_known_good: number;
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

// ── the store ───────────────────────────────────────────────────────────────

/**
 * Parse the cached record, or null. Never throws: localStorage is user-writable
 * and shared with every other tab, so a malformed entry is an expected input,
 * not an exception.
 */
export function readCopyLkg(slug: string, storage: Storage | null): CopyLkgRecord | null {
  if (!storage) return null;
  let raw: string | null;
  try {
    raw = storage.getItem(copyLkgKey(slug));
  } catch {
    return null; // Safari private mode, quota, a disabled store — all the same answer.
  }
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isObject(parsed) || !isObject(parsed.values)) return null;
    if (typeof parsed.version_n !== 'number' || typeof parsed.fetched_at !== 'number') return null;
    return {
      version_n: parsed.version_n,
      min_copy_version: typeof parsed.min_copy_version === 'number' ? parsed.min_copy_version : 0,
      fetched_at: parsed.fetched_at,
      values: parsed.values,
    };
  } catch {
    return null;
  }
}

/**
 * Cache what the server just served. A payload already below the floor is not
 * written at all — caching something the server has already retracted only
 * creates a record whose own bound will refuse it.
 */
export function writeCopyLkg(
  slug: string,
  payload: CopyPayload,
  now: number,
  storage: Storage | null,
): void {
  if (!storage) return;
  if (payload.version_n < payload.min_copy_version) return;
  const record: CopyLkgRecord = {
    version_n: payload.version_n,
    min_copy_version: payload.min_copy_version,
    values: payload.values,
    fetched_at: now,
  };
  try {
    storage.setItem(copyLkgKey(slug), JSON.stringify(record));
  } catch {
    // A full or unavailable store costs us the offline path, nothing more.
  }
}

/** Both bounds. `floor` overrides the record's own when the server has just
 *  told us a newer one. */
export function isCopyLkgUsable(
  record: CopyLkgRecord,
  { now, floor }: { now: number; floor?: number },
): boolean {
  const age = now - record.fetched_at;
  // A negative age means the clock moved, not that the record is fresh.
  if (age < 0 || age > COPY_LKG_TTL_MS) return false;
  return record.version_n >= (floor ?? record.min_copy_version);
}

// ── the chain ───────────────────────────────────────────────────────────────

/**
 * Pick the override set to resolve against, and say where it came from.
 *
 * `payload` is null when the read failed. A payload whose `version_n` is below
 * its own `min_copy_version` is refused too: that is what a MANUAL floor raise
 * looks like — the retraction lever for a document whose newest published
 * version is itself the problem — and it has to reach online families or it
 * only half-works.
 */
export function resolveCopyValues(
  payload: CopyPayload | null,
  lkg: CopyLkgRecord | null,
  now: number,
): { values: Record<string, unknown>; origin: CopyOrigin } {
  if (payload && payload.version_n >= payload.min_copy_version) {
    return { values: payload.values, origin: 'published' };
  }
  const floor = payload?.min_copy_version;
  if (lkg && isCopyLkgUsable(lkg, { now, floor })) {
    return { values: lkg.values, origin: 'last_known_good' };
  }
  return { values: {}, origin: 'seed' };
}

type ValueForKind<Kind> =
  Kind extends 'prose' | 'url' ? string
    : Kind extends 'composed' ? ComposedValue
      : Kind extends 'linked' ? LinkedValue
        : Kind extends 'list' ? Array<Record<string, unknown>>
          : never;

export type CopyValueFor<K extends CopySlotKey> = ValueForKind<(typeof COPY_SLOTS)[K]['kind']>;

/**
 * The slots that resolve to a plain string, and the slots that a `<CopySpan>`
 * can render. Consumers declare which holes take which kind of slot with these,
 * so filing a `list` slot where a headline goes is a type error at the wiring
 * site rather than an `[object Object]` on a family's screen.
 */
export type ProseSlotKey =
  { [K in CopySlotKey]: CopyValueFor<K> extends string ? K : never }[CopySlotKey];

export type SpanSlotKey =
  { [K in CopySlotKey]: CopyValueFor<K> extends string | ComposedValue | LinkedValue ? K : never }[CopySlotKey];

const isSpanInside = (span: unknown, text: string): boolean =>
  Array.isArray(span) && span.length === 2
  && Number.isInteger(span[0]) && Number.isInteger(span[1])
  && span[0] >= 0 && span[0] < span[1] && span[1] <= text.length;

const isTrfHttpsUrl = (href: unknown): boolean => {
  if (typeof href !== 'string') return false;
  if (isTrfMailto(href)) return true;
  try {
    const url = new URL(href);
    return url.protocol === 'https:' && ALLOWED_COPY_HOSTS.includes(url.hostname);
  } catch {
    return false;
  }
};

/**
 * Does this stored value still fit the slot it is filed under?
 *
 * The server validates on write, so a value that reaches here through the front
 * door is sound. Two ways one arrives unsound anyway: the cache outlives a
 * registry change that alters a slot's `kind`, and localStorage is writable by
 * anything running in the page. Either way the answer is the same — render the
 * shipped seed, never `[object Object]` and never an off-domain link on a
 * pre-auth screen a just-rejected family is reading.
 */
function matchesKind(kind: string, value: unknown): boolean {
  switch (kind) {
    case 'prose':
      return typeof value === 'string' && value.length > 0;
    case 'url':
      return isTrfHttpsUrl(value);
    case 'composed':
      return isObject(value) && typeof value.text === 'string' && value.text.length > 0
        && (value.emphasis === null || isSpanInside(value.emphasis, value.text));
    case 'linked':
      return isObject(value) && typeof value.text === 'string' && value.text.length > 0
        && isSpanInside(value.link, value.text) && isTrfHttpsUrl(value.href);
    case 'list':
      return Array.isArray(value) && value.length > 0 && value.every(isObject);
    default:
      return false;
  }
}

/** One slot: the override if there is a usable one, otherwise the seed.
 *
 *  A prose override must carry exactly the `{{name}}` placeholders its seed
 *  carries (#2109 R3-B17). `apply.status.submitted_prefix` reads
 *  “Submitted {{date}}”; an override that dropped `{{date}}` would silently
 *  drop the real date from a family’s screen, and one that invented
 *  `{{name}}` would print the braces. Either is refused for the seed. */
export function resolveCopySlot<K extends CopySlotKey>(
  key: K,
  values: Record<string, unknown>,
): CopyValueFor<K> {
  const slot = COPY_SLOTS[key];
  const override = values[key];
  if (override !== undefined && matchesKind(slot.kind, override)
    && (slot.kind !== 'prose' || samePlaceholders(override as string, slot.seed as string))) {
    return override as CopyValueFor<K>;
  }
  return slot.seed as unknown as CopyValueFor<K>;
}

// ── other languages (#2109 R3-B17, brief B8) ────────────────────────────────
//
// For `en` the chain above is the whole story. For any other language the
// `copy` catalog entry is tried FIRST, and the chain above is what it falls
// back to. Three rules decide whether the catalog entry is used:
//
//   1. STALE. The catalog was translated from the English catalog entry
//      (`src/locales/en/copy.json`, the seed of record), and its state
//      `sourceHash` hashes that English. When staff have published an override,
//      the EFFECTIVE English differs from what was translated, so the
//      translation is out of date and the reader sees the English override —
//      never an old translation of wording TRF has since changed. Comparing the
//      effective English with the en catalog entry byte for byte is the same
//      test as comparing their hashes, without shipping a hash to the browser.
//   2. SHAPE. `composed` and `linked` slots store `[start, end]` offsets into
//      the English. Offsets cannot survive translation, so their catalog entries
//      carry inline markup instead (`<em>…</em>`, `<a>…</a>`), converted to
//      offsets here per language. Malformed markup falls back to English.
//   3. PLACEHOLDERS. A translation must carry exactly the English entry’s
//      `{{name}}` set, or it falls back to English.
//
// Per-language overrides are follow-up #2117: staff edit English only.

/** The i18next namespace that holds the copy store’s translations. */
export const COPY_NAMESPACE = 'copy';

const PLACEHOLDER = /\{\{\s*([\w.]+)\s*\}\}/g;

/** The sorted, de-duplicated `{{name}}` set a string carries. */
export function copyPlaceholders(text: string): string[] {
  return [...new Set([...text.matchAll(PLACEHOLDER)].map((m) => m[1]))].sort();
}

function samePlaceholders(a: string, b: string): boolean {
  return copyPlaceholders(a).join('\u0000') === copyPlaceholders(b).join('\u0000');
}

/** Fill `{{name}}` from params. A name with no param is left as written, so a
 *  missing argument is visible in a test rather than rendering “undefined”. */
export function interpolateCopy(text: string, params?: Record<string, unknown>): string {
  if (!params) return text;
  return text.replace(PLACEHOLDER, (whole, name: string) =>
    (Object.prototype.hasOwnProperty.call(params, name) && params[name] != null ? String(params[name]) : whole));
}

const EM = /<em>([\s\S]*?)<\/em>/g;
const ANCHOR = /<a>([\s\S]*?)<\/a>/g;
const ANY_TAG = /<\/?[a-z][^>]*>/i;

/** `composed` as catalog markup: the emphasised span wrapped in `<em>…</em>`. */
export function toComposedMarkup(value: ComposedValue): string {
  if (!value.emphasis) return value.text;
  const [start, end] = value.emphasis;
  return `${value.text.slice(0, start)}<em>${value.text.slice(start, end)}</em>${value.text.slice(end)}`;
}

/** `linked` as catalog markup: the link span wrapped in `<a>…</a>`. The href is
 *  not part of the entry — a translation never moves where a link goes. */
export function toLinkedMarkup(value: LinkedValue): string {
  const [start, end] = value.link;
  return `${value.text.slice(0, start)}<a>${value.text.slice(start, end)}</a>${value.text.slice(end)}`;
}

/** One `<tag>…</tag>` pair, or none when `optional`; anything else is null. */
function spanFromMarkup(
  markup: string,
  pattern: RegExp,
  optional: boolean,
): { text: string; span: [number, number] | null } | null {
  const matches = [...markup.matchAll(pattern)];
  if (matches.length > 1) return null;
  if (matches.length === 0) {
    if (!optional || ANY_TAG.test(markup)) return null;
    return markup.length > 0 ? { text: markup, span: null } : null;
  }
  const [whole, inner] = matches[0];
  const before = markup.slice(0, matches[0].index);
  const after = markup.slice((matches[0].index ?? 0) + whole.length);
  if (ANY_TAG.test(before) || ANY_TAG.test(inner) || ANY_TAG.test(after)) return null;
  if (inner.trim() === '') return null;
  return { text: before + inner + after, span: [before.length, before.length + inner.length] };
}

/** Catalog markup → a `composed` value, or null when the markup is malformed. */
export function parseComposedMarkup(markup: string): ComposedValue | null {
  const parsed = spanFromMarkup(markup, EM, true);
  return parsed ? { text: parsed.text, emphasis: parsed.span } : null;
}

/** Catalog markup → a `linked` value carrying the ENGLISH href, or null. */
export function parseLinkedMarkup(markup: string, href: string): LinkedValue | null {
  const parsed = spanFromMarkup(markup, ANCHOR, false);
  return parsed && parsed.span ? { text: parsed.text, link: parsed.span, href } : null;
}

/** A resolved English value as the string the en catalog would hold for it. */
export function copyValueAsCatalogEntry(kind: string, value: unknown): string | null {
  if (kind === 'prose' || kind === 'url') return typeof value === 'string' ? value : null;
  if (kind === 'composed') return toComposedMarkup(value as ComposedValue);
  if (kind === 'linked') return toLinkedMarkup(value as LinkedValue);
  return null;
}

export const isEnglishLanguage = (language: string | undefined): boolean =>
  !language || language === 'en' || language.startsWith('en-');

export interface CopyTranslation {
  /** The reader’s language (`es`, `pt`, …). */
  language: string | undefined;
  /** The en catalog entry for the slot: the English the translation was made from. */
  source: unknown;
  /** The target catalog entry for the slot, `''` or undefined when untranslated. */
  target: unknown;
}

/**
 * R3-B17: one slot in the reader’s language. English, an untranslated slot, a
 * stale translation and a malformed one all return the English chain’s answer.
 */
export function resolveCopySlotLocalized<K extends CopySlotKey>(
  key: K,
  values: Record<string, unknown>,
  { language, source, target }: CopyTranslation,
): CopyValueFor<K> {
  const english = resolveCopySlot(key, values);
  if (isEnglishLanguage(language)) return english;
  if (typeof target !== 'string' || target === '' || typeof source !== 'string') return english;

  const { kind } = COPY_SLOTS[key];
  // Rule 1: the translation describes `source`; it is current only while the
  // English a family would otherwise read is still exactly that.
  if (copyValueAsCatalogEntry(kind, english) !== source) return english;
  // Rule 3.
  if (!samePlaceholders(target, source)) return english;

  if (kind === 'prose') return (ANY_TAG.test(target) ? english : target) as CopyValueFor<K>;
  if (kind === 'composed') {
    const parsed = parseComposedMarkup(target);
    const sourceHasEmphasis = (english as ComposedValue).emphasis !== null;
    if (!parsed || (parsed.emphasis !== null) !== sourceHasEmphasis) return english;
    return parsed as CopyValueFor<K>;
  }
  if (kind === 'linked') {
    const parsed = parseLinkedMarkup(target, (english as LinkedValue).href);
    return (parsed ?? english) as CopyValueFor<K>;
  }
  return english;
}

// ── the fallback counters (D-CS-3.5) ────────────────────────────────────────
//
// Reported on the NEXT successful published-copy read, never on a beacon of
// their own: a new pre-auth write endpoint would be trivially spammable into a
// fake alarm, and the server already handles this request and knows the
// client's resolved version. Seed and last-known-good are counted apart
// because they mean different things — one says families are seeing the
// bundle, the other says they are seeing wording TRF has since moved on from.

const ZERO: CopyFallbackCounts = { seed: 0, last_known_good: 0 };

const clampCount = (v: unknown): number =>
  typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0;

export function readFallbackCounts(storage: Storage | null): CopyFallbackCounts {
  if (!storage) return { ...ZERO };
  try {
    const parsed: unknown = JSON.parse(storage.getItem(COPY_FALLBACK_COUNTER_KEY) ?? 'null');
    if (!isObject(parsed)) return { ...ZERO };
    return { seed: clampCount(parsed.seed), last_known_good: clampCount(parsed.last_known_good) };
  } catch {
    return { ...ZERO };
  }
}

function writeFallbackCounts(counts: CopyFallbackCounts, storage: Storage | null): void {
  if (!storage) return;
  try {
    storage.setItem(COPY_FALLBACK_COUNTER_KEY, JSON.stringify(counts));
  } catch {
    // Losing a diagnostic counter is not worth breaking a family's screen over.
  }
}

export function recordFallback(origin: CopyOrigin, storage: Storage | null): void {
  if (origin === 'published') return;
  const counts = readFallbackCounts(storage);
  counts[origin] += 1;
  writeFallbackCounts(counts, storage);
}

/**
 * Subtract what a report actually carried — not "reset to zero". A fallback
 * recorded while the report was in flight belongs to the next report, and
 * zeroing would silently eat it.
 */
export function consumeFallbackCounts(sent: CopyFallbackCounts, storage: Storage | null): void {
  // Nothing was reported, so there is nothing to subtract. Returning early
  // keeps the common case — every read on a healthy platform — from writing a
  // row of zeroes into every family's browser.
  if (clampCount(sent.seed) === 0 && clampCount(sent.last_known_good) === 0) return;
  const counts = readFallbackCounts(storage);
  writeFallbackCounts(
    {
      seed: Math.max(0, counts.seed - clampCount(sent.seed)),
      last_known_good: Math.max(0, counts.last_known_good - clampCount(sent.last_known_good)),
    },
    storage,
  );
}
